import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Navigation, Timer, UserCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { NavTrip } from "@/components/driver/NavTrip";
import { RideAlerts, ringAlert } from "@/components/driver/RideAlerts";
import { useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, kina } from "@/lib/api";
import { usePoll } from "@/lib/usePoll";
import { useDriverGps } from "@/lib/useDriverGps";

const GpsStatus = ({ gps }) => (
  <p data-testid="driver-gps-status" className={`flex items-center gap-2 text-xs ${gps.error ? "text-red-600" : "text-emerald-600"}`}>
    <span className={`h-2 w-2 rounded-full ${gps.error ? "bg-red-500" : gps.pos ? "bg-emerald-500" : "bg-slate-300"}`} />
    {gps.error || (gps.pos ? "Sharing live location with passengers" : "Getting your GPS location…")}
  </p>
);

const Countdown = ({ until }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => {
      clearInterval(id);
    };
  }, []);
  const s = Math.max(0, Math.round((new Date(until) - Date.now()) / 1000));
  return <span className="font-mono-num">{s}s</span>;
};

const RequestCard = ({ r, me, onAccept, onDecline }) => (
  <div data-testid={`ride-request-${r.id}`} className={`rounded-2xl border bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-900/5 ${r.offered_to === me ? "border-amber-400 ring-2 ring-amber-200" : "border-blue-100"}`}>
    {r.offered_to === me && r.offer_expires_at && (
      <p data-testid={`offer-countdown-${r.id}`} className="mb-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800">
        <Timer className="h-3.5 w-3.5" /> {r.requested_driver_id === me ? "Passenger chose you" : "You're the nearest taxi"} · respond in <Countdown until={r.offer_expires_at} />
      </p>
    )}
    {r.requested_driver_id === me && r.offered_to !== me && <p className="mb-2 flex items-center gap-1 text-xs font-semibold text-blue-700"><UserCheck className="h-3.5 w-3.5" /> Passenger requested you</p>}
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 space-y-1.5 text-sm">
        <p className="flex items-center gap-2 text-slate-900"><MapPin className="h-4 w-4 shrink-0 text-blue-700" /><span className="truncate">{r.pickup.address}</span></p>
        <p className="flex items-center gap-2 text-slate-600"><Navigation className="h-4 w-4 shrink-0 text-amber-500" /><span className="truncate">{r.dropoff.address}</span></p>
        {r.scheduled_for ? (
          <p data-testid={`scheduled-badge-${r.id}`} className="inline-block rounded-full bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700">Scheduled pickup · {fmtDate(r.scheduled_for)}</p>
        ) : (
          <p className="text-xs text-slate-400">{r.distance_km} km trip{r.pickup_km != null ? ` · pickup ${r.pickup_km} km from you` : ""} · {fmtDate(r.created_at)}</p>
        )}
      </div>
      <p className="whitespace-nowrap font-mono-num text-xl font-semibold text-slate-900">{kina(r.fare)}</p>
    </div>
    <div className="mt-4 flex gap-2">
      <button data-testid={`decline-ride-button-${r.id}`} onClick={() => onDecline(r.id)} className="h-11 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50">Decline</button>
      <button data-testid={`accept-ride-button-${r.id}`} onClick={() => onAccept(r.id)} className="h-11 flex-1 rounded-xl bg-blue-700 text-sm font-semibold text-white transition-all hover:bg-blue-600 active:scale-[0.98]">Accept ride</button>
    </div>
  </div>
);

export const Dispatch = () => {
  const { user, refresh } = useAuth();
  const [active, setActive] = useState(undefined);
  const [requests, setRequests] = useState([]);
  const gps = useDriverGps(Boolean(user.is_online || active));
  const seen = useRef(null);

  const load = async () => {
    const [a, r] = await Promise.all([api.get("/driver/active"), api.get("/driver/requests")]);
    setActive(a.data);
    setRequests(r.data);
    const ids = r.data.map((x) => `${x.id}:${x.offer_expires_at || "open"}`);
    if (seen.current && ids.some((id) => !seen.current.includes(id))) ringAlert();
    seen.current = ids;
  };
  usePoll(() => load().catch(() => {}), 5000, [user.is_online]);

  const toggle = async (online) => {
    try {
      await api.post("/driver/online", { online });
      await refresh();
      toast.success(online ? "You're online — requests will appear here" : "You're offline");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const accept = async (id) => {
    try {
      await api.post(`/bookings/${id}/accept`);
      toast.success("Ride accepted — head to pickup");
      load();
    } catch (e) {
      toast.error(errMsg(e));
      load();
    }
  };

  const decline = async (id) => {
    await api.post(`/bookings/${id}/decline`).catch(() => {});
    toast.message("Ride passed to the next nearest taxi");
    load();
  };

  if (active === undefined) return <div className="h-64 animate-pulse rounded-2xl bg-white" />;
  if (active) return <NavTrip trip={active} onUpdate={load} gps={gps} GpsStatus={GpsStatus} />;
  return (
    <div className="space-y-6">
      <div className={`flex items-center justify-between rounded-2xl p-6 ${user.is_online ? "bg-blue-700 text-white" : "border border-blue-100 bg-white"}`}>
        <div>
          <p className="text-lg font-semibold" data-testid="driver-online-status">{user.is_online ? "You're online" : "You're offline"}</p>
          <p className={`text-sm ${user.is_online ? "text-blue-100" : "text-slate-500"}`}>{user.is_online ? "Listening for ride requests across Port Moresby" : "Go online to start receiving ride requests"}</p>
          {user.is_online && <div className="mt-2 rounded-full bg-white/90 px-3 py-1"><GpsStatus gps={gps} /></div>}
        </div>
        <Switch data-testid="driver-online-toggle" checked={user.is_online} onCheckedChange={toggle} className="data-[state=checked]:bg-amber-400" />
      </div>
      <RideAlerts />
      {user.is_online && (
        <div>
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500">Open requests ({requests.length})</p>
          {requests.length === 0 ? (
            <p data-testid="no-requests-message" className="rounded-2xl border border-dashed border-blue-200 bg-white p-10 text-center text-slate-500">No ride requests right now. Hang tight.</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{requests.map((r) => <RequestCard key={r.id} r={r} me={user.id} onAccept={accept} onDecline={decline} />)}</div>
          )}
        </div>
      )}
    </div>
  );
};
